/**
 * API enum name → its Vibe variable name and hexes. Ordered as in §7.4 of the spec, which is
 * the order the reference table uses, not the order of `STATUS_COLORS`.
 */
export const statusPalette = Object.freeze({
    grass_green: { cssName: 'grass_green', base: '#037f4c', hover: '#116846', selected: '#81bfa5' },
    done_green: { cssName: 'done-green', base: '#00c875', hover: '#0f9b63', selected: '#80e3ba' },
    bright_green: { cssName: 'bright-green', base: '#9cd326', hover: '#7ca32b', selected: '#cde992' },
    saladish: { cssName: 'saladish', base: '#cab641', hover: '#9d8f3e', selected: '#e4daa0' },
    egg_yolk: { cssName: 'egg_yolk', base: '#ffcb00', hover: '#eaaa15', selected: '#ffe580' },
    working_orange: { cssName: 'working_orange', base: '#fdab3d', hover: '#c0873c', selected: '#fed59e' },
    dark_orange: { cssName: 'dark-orange', base: '#ff6d3b', hover: '#c25531', selected: '#ffb196' },
    peach: { cssName: 'peach', base: '#ffadad', hover: '#c2888a', selected: '#ffd6d6' },
    sunset: { cssName: 'sunset', base: '#ff7575', hover: '#c26163', selected: '#ffbaba' },
    stuck_red: { cssName: 'stuck-red', base: '#df2f4a', hover: '#ad3f51', selected: '#f0a1ad' },
    dark_red: { cssName: 'dark-red', base: '#bb3354', hover: '#92334c', selected: '#dd99a9' },
    sofia_pink: { cssName: 'sofia_pink', base: '#e50073', hover: '#c20062', selected: '#ff8ac4' },
    lipstick: { cssName: 'lipstick', base: '#ff5ac4', hover: '#c24e9a', selected: '#fface1' },
    bubble: { cssName: 'bubble', base: '#faa1f1', hover: '#be80ba', selected: '#fcd0f8' },
    purple: { cssName: 'purple', base: '#9d50dd', hover: '#7d45b0', selected: '#d0aeed' },
    dark_purple: { cssName: 'dark_purple', base: '#784bd1', hover: '#6344a3', selected: '#bba5e8' },
    berry: { cssName: 'berry', base: '#7e3b8a', hover: '#673971', selected: '#be9dc4' },
    dark_indigo: { cssName: 'dark_indigo', base: '#401694', hover: '#3c1f78', selected: '#a08bc9' },
    indigo: { cssName: 'indigo', base: '#5559df', hover: '#4b4ead', selected: '#aaacef' },
    navy: { cssName: 'navy', base: '#225091', hover: '#274776', selected: '#90a7c8' },
    bright_blue: { cssName: 'bright-blue', base: '#579bfc', hover: '#4c7cc1', selected: '#abcdfd' },
    dark_blue: { cssName: 'dark-blue', base: '#007eb5', hover: '#0f6d97', selected: '#80c2df' },
    aquamarine: { cssName: 'aquamarine', base: '#4eccc6', hover: '#469e9b', selected: '#a6e5e2' },
    chili_blue: { cssName: 'chili-blue', base: '#66ccff', hover: '#569ec3', selected: '#b2e5ff' },
    river: { cssName: 'river', base: '#74afcc', hover: '#588095', selected: '#b3d0de' },
    winter: { cssName: 'winter', base: '#9aadbd', hover: '#7b8895', selected: '#ccd6de' },
    explosive: { cssName: 'explosive', base: '#c4c4c4', hover: '#98999a', selected: '#e1e1e1' },
    american_gray: { cssName: 'american_gray', base: '#757575', hover: '#69696a', selected: '#bfbfbf' },
    blackish: { cssName: 'blackish', base: '#333333', hover: null, selected: null },
    brown: { cssName: 'brown', base: '#7f5347', hover: '#684943', selected: '#bfa9a3' },
    orchid: { cssName: 'orchid', base: '#e484bd', hover: '#ae5d8d', selected: '#ecbad7' },
    tan: { cssName: 'tan', base: '#bca58a', hover: '#8a7862', selected: '#d6cabc' },
    sky: { cssName: 'sky', base: '#a1e3f6', hover: '#81b6c5', selected: '#d0f1fa' },
    coffee: { cssName: 'coffee', base: '#cd9282', hover: '#976758', selected: '#dec0b7' },
    royal: { cssName: 'royal', base: '#216edf', hover: '#225eb7', selected: '#95bbf2' },
    teal: { cssName: 'teal', base: '#175a63', hover: '#12484f', selected: '#8bacb1' },
    lavender: { cssName: 'lavender', base: '#bda8f9', hover: '#9786c7', selected: '#ded4fc' },
    steel: { cssName: 'steel', base: '#a9bee8', hover: '#8798ba', selected: '#d4dff4' },
    lilac: { cssName: 'lilac', base: '#9d99b9', hover: '#7e7a94', selected: '#ceccdc' },
    pecan: { cssName: 'pecan', base: '#563e3e', hover: '#453232', selected: '#ab9f9f' },
});
/** The colour an unknown or empty status renders as (edge case E1, spec §12). */
export const EMPTY_STATUS_COLOR = 'explosive';
function isStatusColor(name) {
    return Object.prototype.hasOwnProperty.call(statusPalette, name);
}
/**
 * `statusColorVar('done_green')` → `'--color-done-green'`.
 * `statusColorVar('grass_green')` → `'--color-grass_green'`.
 *
 * An unknown name (imported garbage, edge case E1) resolves to `explosive` rather than throwing.
 */
export function statusColorVar(apiName, state = 'base') {
    const key = isStatusColor(apiName) ? apiName : EMPTY_STATUS_COLOR;
    const spec = statusPalette[key];
    const suffix = state === 'base' ? '' : `-${state}`;
    // `blackish` has no hover/selected variant in Vibe; fall back to its base variable.
    if (state !== 'base' && spec[state] === null)
        return `--color-${spec.cssName}`;
    return `--color-${spec.cssName}${suffix}`;
}
/** The literal hex, for the canvas grid and for tests. Unknown names give `explosive`. */
export function statusColorHex(apiName, state = 'base') {
    const key = isStatusColor(apiName) ? apiName : EMPTY_STATUS_COLOR;
    const spec = statusPalette[key];
    return (state === 'base' ? spec.base : spec[state]) ?? spec.base;
}
/**
 * A `css(var)` expression with the literal hex as its fallback (spec §12 E8: no colour
 * declaration may resolve to nothing).
 */
export function statusColor(apiName, state = 'base') {
    return `var(${statusColorVar(apiName, state)}, ${statusColorHex(apiName, state)})`;
}
/**
 * Black text on a light swatch, white on a dark one — WCAG relative brightness, not a
 * per-colour table (spec M0-F7 §6.4).
 *
 * Exported because a board's own `labels_colors` hex is **not** a palette name: the Tasks
 * section paints its status pill with whatever hex the board stores (P-267), and a second copy
 * of this formula would be a second definition of "readable".
 */
export function readableTextOn(hex) {
    const value = hex.replace('#', '');
    if (!/^[0-9a-fA-F]{6}$/.test(value))
        return '#ffffff';
    const r = parseInt(value.slice(0, 2), 16);
    const g = parseInt(value.slice(2, 4), 16);
    const b = parseInt(value.slice(4, 6), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.72 ? 'var(--primary-text-color, #323338)' : '#ffffff';
}
/**
 * `FolderColor` is a 16-value upper-cased subset of the same palette plus `NULL` (spec §8).
 * `folderColorVar('DONE_GREEN')` is exactly `statusColorVar('done_green')`.
 */
export const FOLDER_COLORS = [
    'AQUAMARINE',
    'BRIGHT_BLUE',
    'BRIGHT_GREEN',
    'CHILI_BLUE',
    'DARK_ORANGE',
    'DARK_PURPLE',
    'DARK_RED',
    'DONE_GREEN',
    'INDIGO',
    'LIPSTICK',
    'NULL',
    'PURPLE',
    'SOFIA_PINK',
    'STUCK_RED',
    'SUNSET',
    'WORKING_ORANGE',
];
export function folderColorVar(name) {
    if (!name || name.toUpperCase() === 'NULL')
        return '--secondary-text-color';
    return statusColorVar(name.toLowerCase());
}
export function folderColor(name) {
    if (!name || name.toUpperCase() === 'NULL')
        return 'var(--secondary-text-color, #676879)';
    return statusColor(name.toLowerCase());
}
/** `FolderFontWeight` → Vibe's numeric weights (spec §8). */
export const FOLDER_FONT_WEIGHTS = {
    FONT_WEIGHT_VERY_LIGHT: 200,
    LIGHT: 300,
    NORMAL: 400,
    BOLD: 500,
    NULL: 400,
};
export function folderFontWeight(name) {
    if (!name)
        return FOLDER_FONT_WEIGHTS.NORMAL;
    const key = name.toUpperCase();
    return FOLDER_FONT_WEIGHTS[key] ?? FOLDER_FONT_WEIGHTS.NORMAL;
}
//# sourceMappingURL=status-palette.js.map